// Ports of the Android overview's PaceSplits, PaceSplitSort and PaceSplitLabels, so a split reads the
// same on every platform.

import type { MeasurementPrefUtility } from '../helpers/MeasurementPref';
import { elevationGainFromAltitudes, elevationLossFromAltitudes } from '../elevation/elevationTotals';
import { MINIMUM_PACE_SPREAD_SECONDS_PER_METER, rampColor, type ColorScale } from './colorScale';
import { paceUnit, paceValue, toSmallUnit } from './format';
import type { ProfileSample } from './profiles';
import { isMeasured, movingStretches, sum, valueAt } from './timeline';

/**
 * What the pace graph was read from, kept so the splits can be cut again at another length.
 * `distances` and `elapsedSeconds` are one per track point; `heights` are the points that have one,
 * at `heightMeters` along the track.
 */
export interface PaceSplitTrack {
  distances: number[];
  elapsedSeconds: number[];
  heightMeters: number[];
  heights: number[];
}

export function paceSplitTrack(
  distances: number[],
  elapsedMs: number[],
  altitudes: Array<number | null | undefined>
): PaceSplitTrack {
  const heighted = distances.map((_, i) => i).filter((i) => Number.isFinite(altitudes[i]));
  const heights = heighted.map((i) => altitudes[i] as number);
  const hasHeights = heights.some((height) => height !== 0);
  return {
    distances,
    elapsedSeconds: elapsedMs.map((ms) => ms / 1000),
    heightMeters: hasHeights ? heighted.map((i) => distances[i]) : [],
    heights: hasHeights ? heights : [],
  };
}

/** `secondsPerMeter` is null where no ground was walked; the heights are null for a track without any. */
export interface PaceSplit {
  startMeters: number;
  endMeters: number;
  movingSeconds: number;
  secondsPerMeter: number | null;
  elevationChangeMeters: number | null;
  gainMeters: number | null;
  lossMeters: number | null;
  averageBpm: number | null;
}

/** The track cut every `lengthMeters`; the last split is whatever is left. */
export interface PaceSplits {
  splits: PaceSplit[];
  lengthMeters: number;
  totalMeters: number;
  fastestIndex: number;
  slowestIndex: number;
}

/**
 * Moving pace every `lengthMeters`, from the same stretches as the pace graph, so pauses and the jumps
 * between continued legs are left out. Fastest and slowest skip a sliver too short to measure, such as
 * a few metres left at the end. Null when no split has ground enough to measure.
 */
export function paceSplitsOf(
  track: PaceSplitTrack,
  lengthMeters: number,
  heartRate: ProfileSample[] | null = null
): PaceSplits | null {
  const total = track.distances[track.distances.length - 1] ?? 0;
  if (!(total > 0) || !(lengthMeters > 0) || track.distances.length !== track.elapsedSeconds.length) return null;
  const count = splitCount(total, lengthMeters);
  const stretches = movingStretches(track.distances, track.elapsedSeconds, count, lengthMeters);

  const splits = stretches.map((stretch, index): PaceSplit => {
    const isLast = index === count - 1;
    const start = index * lengthMeters;
    const end = isLast ? total : (index + 1) * lengthMeters;
    const heights = heightsWithin(track, start, end);
    const bpms = (heartRate ?? [])
      .filter((sample) => sample.distance >= start && (sample.distance < end || isLast))
      .map((sample) => sample.value);
    return {
      startMeters: start,
      endMeters: end,
      movingSeconds: stretch.seconds,
      secondsPerMeter: stretch.meters > 0 && stretch.seconds > 0 ? stretch.seconds / stretch.meters : null,
      elevationChangeMeters: heights ? heights[heights.length - 1] - heights[0] : null,
      gainMeters: heights ? elevationGainFromAltitudes(heights) : null,
      lossMeters: heights ? elevationLossFromAltitudes(heights) : null,
      averageBpm: bpms.length > 0 ? Math.round(sum(bpms) / bpms.length) : null,
    };
  });

  const measured = splits
    .map((_, i) => i)
    .filter((i) => splits[i].secondsPerMeter !== null && isMeasured(stretches[i], lengthMeters));
  if (measured.length === 0) return null;
  const pace = (i: number) => splits[i].secondsPerMeter!;
  return {
    splits,
    lengthMeters,
    totalMeters: total,
    fastestIndex: measured.reduce((best, i) => (pace(i) < pace(best) ? i : best)),
    slowestIndex: measured.reduce((worst, i) => (pace(i) > pace(worst) ? i : worst)),
  };
}

/** The heights from `start` to `end` metres, the ends read off the track between its points. */
function heightsWithin(track: PaceSplitTrack, start: number, end: number): number[] | null {
  if (track.heights.length < 2) return null;
  const inside = track.heights.filter((_, i) => track.heightMeters[i] > start && track.heightMeters[i] < end);
  return [valueAt(start, track.heightMeters, track.heights), ...inside, valueAt(end, track.heightMeters, track.heights)];
}

/** The split under `distance` metres along the track. */
export function splitIndexAt(splits: PaceSplits, distance: number): number {
  return Math.min(Math.max(Math.floor(distance / splits.lengthMeters), 0), splits.splits.length - 1);
}

export const BAR_FLOOR = 0.2;

/** 0..1, fastest tallest and slowest a fifth, over no less than the pace graph's spread so a steady walk stays level. */
export function barFraction(splits: PaceSplits, index: number): number | null {
  const pace = splits.splits[index].secondsPerMeter;
  if (pace === null) return null;
  const fastest = splits.splits[splits.fastestIndex].secondsPerMeter!;
  const slowest = splits.splits[splits.slowestIndex].secondsPerMeter!;
  const spread = Math.max(slowest - fastest, MINIMUM_PACE_SPREAD_SECONDS_PER_METER);
  const slowEnd = (fastest + slowest + spread) / 2;
  return BAR_FLOOR + (1 - BAR_FLOOR) * Math.min(Math.max((slowEnd - pace) / spread, 0), 1);
}

/** The scale's colour at `value`, low colour to high. */
export function scaleColor(scale: ColorScale, value: number): string {
  return rampColor([scale.lowColor, scale.highColor], (value - scale.low) / (scale.high - scale.low));
}

/** Split lengths on offer, in the user's big unit. */
export const SPLIT_STEPS = [0.25, 0.5, 1, 2, 5, 10, 25];

/** A longer hike starts on a longer split rather than a crowd of bars. */
export const MOST_DEFAULT_SPLITS = 30;

/** Past this the bars are too thin to pick out. */
export const MOST_SPLITS = 100;

/** A track ending a rounding error past a boundary doesn't get a split of its own. */
export function splitCount(totalMeters: number, lengthMeters: number): number {
  return Math.max(Math.ceil((totalMeters - 1e-6) / lengthMeters), 1);
}

/** Quarters under one unit, halves under three, then the shortest whole step that keeps to MOST_DEFAULT_SPLITS. */
export function defaultSplitStep(totalMeters: number, metersPerUnit: number): number {
  const units = totalMeters / metersPerUnit;
  if (units < 1) return 0.25;
  if (units < 3) return 0.5;
  return (
    SPLIT_STEPS.filter((step) => step >= 1).find((step) => splitCount(totalMeters, step * metersPerUnit) <= MOST_DEFAULT_SPLITS) ??
    SPLIT_STEPS[SPLIT_STEPS.length - 1]
  );
}

/** Splits only read as splits with at least two. */
export function hasSplits(totalMeters: number, metersPerUnit: number): boolean {
  return totalMeters > 0 && splitCount(totalMeters, SPLIT_STEPS[0] * metersPerUnit) >= 2;
}

export function shorterSplitStep(step: number, totalMeters: number, metersPerUnit: number): number | null {
  const shorter = [...SPLIT_STEPS].reverse().find((s) => s < step);
  return shorter !== undefined && splitCount(totalMeters, shorter * metersPerUnit) <= MOST_SPLITS ? shorter : null;
}

export function longerSplitStep(step: number, totalMeters: number, metersPerUnit: number): number | null {
  const longer = SPLIT_STEPS.find((s) => s > step);
  return longer !== undefined && splitCount(totalMeters, longer * metersPerUnit) >= 2 ? longer : null;
}

/** The All splits table's columns it can be ordered by; "vs avg" orders as pace does. */
export type SplitColumn = 'distance' | 'time' | 'pace' | 'elevation' | 'heartRate';

/** The splits' indices ordered by `column`, those without a reading in it last either way. */
export function sortedSplitIndices(splits: PaceSplits, column: SplitColumn, isDescending: boolean): number[] {
  const key = (split: PaceSplit): number | null => {
    switch (column) {
      case 'distance': return split.startMeters;
      case 'time': return split.secondsPerMeter === null ? null : split.movingSeconds;
      case 'pace': return split.secondsPerMeter;
      case 'elevation': return split.elevationChangeMeters;
      case 'heartRate': return split.averageBpm;
    }
  };
  const indices = splits.splits.map((_, i) => i);
  const known = indices.filter((i) => key(splits.splits[i]) !== null).sort((a, b) => key(splits.splits[a])! - key(splits.splits[b])!);
  const unknown = indices.filter((i) => key(splits.splits[i]) === null);
  return [...(isDescending ? known.reverse() : known), ...unknown];
}

/** "0.25", "1", "5.3": at most two decimals, none trailing. */
export function splitDistanceLabel(units: number): string {
  return String(Number(units.toFixed(2)));
}

/** "5", where the split ends in the user's big unit. */
export function splitEndLabel(split: PaceSplit, pref: MeasurementPrefUtility): string {
  return splitDistanceLabel(split.endMeters / pref.metresFromBigUnit(1));
}

/** "12–13.2 km". */
export function splitRangeLabel(split: PaceSplit, pref: MeasurementPrefUtility): string {
  const start = splitDistanceLabel(split.startMeters / pref.metresFromBigUnit(1));
  return `${start}–${splitEndLabel(split, pref)} ${pref.kilometerSymbol()}`;
}

/** "20m 50s", or "2h 24m" from an hour. */
export function splitDurationLabel(seconds: number): string {
  const total = Math.max(Math.round(seconds), 0);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const pad = (n: number) => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}h ${pad(minutes)}m` : `${minutes}m ${pad(total % 60)}s`;
}

/** Whole seconds per big unit slower (positive) or faster (negative) than `averageSecondsPerMeter`. */
export function paceDifferenceSeconds(secondsPerMeter: number, averageSecondsPerMeter: number, pref: MeasurementPrefUtility): number {
  return Math.round((secondsPerMeter - averageSecondsPerMeter) * pref.metresFromBigUnit(1));
}

/** "+0:39", "−0:29", or "0:00" for level with the average. */
export function paceDifferenceLabel(seconds: number): string {
  const sign = seconds > 0 ? '+' : seconds < 0 ? '−' : '';
  const magnitude = Math.abs(seconds);
  return `${sign}${Math.floor(magnitude / 60)}:${String(magnitude % 60).padStart(2, '0')}`;
}

/** "+22 m", "−30 m", "0 m" once it rounds to nothing; without `withUnit` for a column headed by it. */
export function elevationChangeLabel(meters: number, pref: MeasurementPrefUtility, withUnit = true): string {
  const rounded = Math.round(Math.abs(toSmallUnit(meters, pref)));
  const sign = rounded === 0 ? '' : meters > 0 ? '+' : '−';
  return withUnit ? `${sign}${rounded} ${pref.meterSymbol()}` : `${sign}${rounded}`;
}

/** "+1 / −32 m": the climbing and the descending within a split. */
export function climbAndDescentLabel(gainMeters: number, lossMeters: number, pref: MeasurementPrefUtility): string {
  const rounded = (meters: number) => Math.round(toSmallUnit(meters, pref));
  return `+${rounded(gainMeters)} / −${rounded(lossMeters)} ${pref.meterSymbol()}`;
}

/** "3.1", the big unit an hour at an average pace. */
export function averageSpeedValue(secondsPerMeter: number, pref: MeasurementPrefUtility, locale?: string): string {
  return (3600 / (secondsPerMeter * pref.metresFromBigUnit(1))).toLocaleString(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

/** Wide enough for a bar's "15:58" at the chart's label size. */
export const LABEL_CHARACTER_PX = 7.5;
const LABEL_GAP_PX = 4;

function labelWidth(text: string): number {
  return text.length * LABEL_CHARACTER_PX;
}

/** Whether every bar's time fits over it in `widthPx`; otherwise only the fastest and slowest are labelled. */
export function barLabelsFit(splits: PaceSplits, labels: Array<string | null>, widthPx: number): boolean {
  const widest = Math.max(0, ...labels.map((label) => (label ? labelWidth(label) : 0)));
  return widest + LABEL_GAP_PX <= (splits.lengthMeters / splits.totalMeters) * widthPx;
}

export interface SplitAxisLabel {
  /** 0..1 across the chart: under the middle of its split, kept inside the chart. */
  left: number;
  text: string;
}

/** Each split's end under its bar, the last with the unit, dropping any that would run into the one before or the last. */
export function splitAxisLabels(splits: PaceSplits, pref: MeasurementPrefUtility, widthPx: number): SplitAxisLabel[] {
  if (!(widthPx > 0)) return [];
  const all = splits.splits.map((split, i) => {
    const text = i === splits.splits.length - 1 ? `${splitEndLabel(split, pref)} ${pref.kilometerSymbol()}` : splitEndLabel(split, pref);
    const center = ((split.startMeters + split.endMeters) / 2 / splits.totalMeters) * widthPx;
    const left = Math.min(Math.max(center - labelWidth(text) / 2, 0), Math.max(widthPx - labelWidth(text), 0));
    return { left, right: left + labelWidth(text), text };
  });
  const spacing = LABEL_GAP_PX * 3;
  const last = all[all.length - 1];

  const kept: typeof all = [];
  all.slice(0, -1).forEach((label) => {
    const previous = kept[kept.length - 1];
    if (previous && label.left < previous.right + spacing) return;
    if (label.right + spacing > last.left) return;
    kept.push(label);
  });
  return [...kept, last].map(({ left, text }) => ({ left: left / widthPx, text }));
}

type Translate = (key: string, params?: Record<string, unknown>) => string;

export interface SplitDetail {
  /** "Split 7 · 12–13.2 km". */
  title: string;
  /** "11:03 /km", or "–" where no ground was walked. */
  pace: string;
  /** "13m 15s", "+1 / −32 m", "140 bpm", each where there is one. */
  lines: string[];
}

export function splitDetail(splits: PaceSplits, index: number, pref: MeasurementPrefUtility, t: Translate): SplitDetail {
  const split = splits.splits[index];
  const lines: string[] = [];
  if (split.secondsPerMeter !== null) lines.push(splitDurationLabel(split.movingSeconds));
  if (split.gainMeters !== null && split.lossMeters !== null) lines.push(climbAndDescentLabel(split.gainMeters, split.lossMeters, pref));
  if (split.averageBpm !== null) lines.push(`${split.averageBpm} ${t('recordings.activity.bpm')}`);
  return {
    title: t('recordings.activity.split_title', { number: index + 1, range: splitRangeLabel(split, pref) }),
    pace: split.secondsPerMeter === null ? '–' : `${paceValue(split.secondsPerMeter, pref)} ${paceUnit(pref)}`,
    lines,
  };
}

/**
 * The elevation profile as an SVG path in a 100 × 100 box, its top a little below the box's and no
 * flatter than `minimumSpread` metres, so it sits behind the bars as it does behind the pace graph.
 */
export function backdropPath(samples: ProfileSample[] | null | undefined, totalMeters: number, minimumSpread: number): string | null {
  if (!samples || samples.length < 2 || !(totalMeters > 0)) return null;
  const values = samples.map((s) => s.value);
  const lowest = Math.min(...values);
  const spread = Math.max(Math.max(...values) - lowest, minimumSpread);
  const round = (n: number) => Math.round(n * 100) / 100;
  const x = (meters: number) => round((Math.min(Math.max(meters, 0), totalMeters) / totalMeters) * 100);
  const y = (value: number) => round(100 - ((value - lowest) / spread) * 90);
  const line = samples.map((s) => `L${x(s.distance)},${y(s.value)}`);
  return [`M0,100`, `L0,${y(samples[0].value)}`, ...line, `L100,${y(samples[samples.length - 1].value)}`, 'L100,100', 'Z'].join(' ');
}
